/**
 * Dữ liệu mẫu cho bản dựng lại giao diện (§14).
 *
 * Dùng **dữ liệu nghiệp vụ tiếng Việt** có chủ đích: tên và nhãn tiếng Việt dài
 * hơn tiếng Anh, nên chúng mới thực sự kiểm được hành vi `truncate` và
 * `min-w-0`. Mỗi danh sách có ít nhất một tên rất dài để thử cắt chuỗi.
 */
import type { UserProfile } from '../stores/auth';

/**
 * Tài khoản siêu quản trị **của cổng nền tảng**: thấy mọi màn quản trị.
 *
 * `SUPER_ADMIN` mở mọi quyền nhưng **không** mở mọi cổng: `lib/pham-vi-cong`
 * xếp vai trò này vào phạm vi `nen-tang`, nên nhóm nghiệp vụ của tenant vẫn
 * không hiện ra. Đó là chủ đích, không phải thiếu sót.
 */
export const MOCK_SUPER_ADMIN: UserProfile = {
  id: 'u-001',
  fullName: 'Nguyễn Văn An',
  email: 'an.nguyen@foxai.vn',
  roles: ['SUPER_ADMIN'],
  permissions: [],
  organizationId: 'org-001',
};

/**
 * Mã quyền của module `tri-thuc` (manifest `modules/tri-thuc/module.yaml`, ADR-0075 mục 3) cho tài
 * khoản mẫu, để màn tri thức hiện đủ nút. Bỏ `tri-thuc:tep:read-all`: quyền đó chỉ trợ lý dùng.
 */
export const QUYEN_TRI_THUC_MAU: string[] = [
  'kho:read', 'kho:create', 'kho:update', 'kho:delete',
  'thu-muc:read', 'thu-muc:create', 'thu-muc:update', 'thu-muc:delete', 'thu-muc:share',
  'tep:read', 'tep:create', 'tep:update', 'tep:delete', 'tep:share',
  'tai-lieu:read', 'tai-lieu:create', 'tai-lieu:update', 'tai-lieu:approve', 'tai-lieu:archive',
].map((p) => `tri-thuc:${p}`);

/**
 * 22 mã quyền của module `bao-cao` (manifest `modules/bao-cao/module.yaml`, ADR-0081) cho tài khoản mẫu, để
 * màn Báo cáo doanh nghiệp hiện đủ nút. `tai-lieu:import` chỉ hệ thống ngoài dùng, nhưng có cũng không sao.
 */
export const QUYEN_BAO_CAO_MAU: string[] = [
  ...['tong-quan', 'tai-chinh', 'du-an', 'kinh-doanh', 'nhan-su'].flatMap((p) => [`${p}:read`, `${p}:update`]),
  'tai-lieu:read', 'tai-lieu:create', 'tai-lieu:update', 'tai-lieu:delete', 'tai-lieu:import',
  'thu-muc:read', 'thu-muc:create', 'thu-muc:update', 'thu-muc:delete',
  'nguon:read', 'nguon:create', 'nguon:update',
].map((p) => `bao-cao:${p}`);

/**
 * Tài khoản hạn chế **của cổng tenant**: dùng để thử `ForbiddenView` ở phần
 * lớn route và kiểm việc ẩn nút thêm/sửa/xóa ở những route còn lại.
 */
export const MOCK_LIMITED_USER: UserProfile = {
  id: 'u-002',
  fullName: 'Trần Thị Bình',
  email: 'binh.tran@khachhang.vn',
  roles: ['Nhân viên nhập liệu'],
  // Cố ý **không** có `TENANT.READ`: dùng để kiểm nhóm quản trị bị ẩn khỏi
  // thanh điều hướng, và `moduleDauTien` không rơi vào vòng lặp chuyển hướng.
  permissions: ['DOC_MGMT.READ', 'OCR_FILES.READ'],
  organizationId: 'org-001',
  tenantId: '0a7d3c5e-1f2b-4e6a-9c8d-000000000002',
};

/**
 * Quản trị viên **của một tổ chức khách hàng** — cổng tenant.
 *
 * Bộ quyền cố ý có `TENANT.READ`: nó kiểm được đúng cái bẫy mà phạm vi sinh ra
 * để chặn. Quyền này mở module `Tenant` của cổng nền tảng, nên nếu luật lọc chỉ
 * xét quyền mà quên xét cổng thì tài khoản này sẽ thấy nhóm quản trị nền tảng.
 */
export const MOCK_TENANT_ADMIN: UserProfile = {
  id: 'u-004',
  fullName: 'Phạm Thu Hà',
  // ADR-0101: tên đăng nhập và email nhận thư khác nhau, như dòng u-004 của mock/nguoi-dung.ts
  tenDangNhap: 'ha.pham@khachhang.vn',
  email: 'phamthuha@gmail.com',
  roles: ['vaiTro.quanTriToChuc'],
  permissions: [
    'TENANT.READ',
    // Trang gốc `/client` (phân hệ DASHBOARD): thiếu thì Dashboard của tổ chức ra "Không có quyền truy cập".
    'DASHBOARD.READ',
    'AI_TOKEN.READ',
    'DOC_MGMT.READ',
    'OCR_FILES.READ',
    'KNOWLEDGE_MGMT.READ',
    'KNOWLEDGE_REVIEW.READ',
    'KNOWLEDGE_UPLOAD.READ',
    ...QUYEN_TRI_THUC_MAU,
    // ADR-0081: Báo cáo doanh nghiệp — route chặn theo phân hệ BIZ_*, nút theo mã `bao-cao:*`.
    'BIZ_OVERVIEW.READ',
    'BIZ_FINANCE.READ',
    'BIZ_PROJECT.READ',
    'BIZ_SALES.READ',
    'BIZ_HR.READ',
    'BIZ_REPORT_DOC.READ',
    ...QUYEN_BAO_CAO_MAU,
    'USER_CONFIG.READ',
    'ROLE_CONFIG.READ',
    // G2-D1-17: Cơ cấu tổ chức — chỉ quản trị tổ chức.
    'ORG_STRUCTURE.READ',
    // ADR-0059 mục 8: Cài đặt bảo mật và nhật ký thao tác của tenant mình — chỉ quản trị tổ chức.
    'SECURITY_CONFIG.READ',
    'SECURITY_CONFIG.UPDATE',
    'AUDIT_EVENTS.READ',
    // ADR-0073: Cấu hình xử lý văn bản (module van-ban) của tenant mình.
    'TEXT_CONFIG.READ',
    'TEXT_CONFIG.CREATE',
    'TEXT_CONFIG.UPDATE',
    'TEXT_CONFIG.DELETE',
    // ADR-0096: quyền của module demo-sdk mẫu (`mock/module-da-cai.ts`); Tenant Admin thật nhận theo ADR-0077 mục 2.
    'demo-sdk:tong-quan:read',
    'demo-sdk:cong-viec:read',
    'demo-sdk:cong-viec:write',
    /*
     * Định danh eKYC. `EKYC_REVIEW` tách khỏi `EKYC_SESSION` có chủ ý
     * (`docs/eKYC/06_TICH_HOP.md` §1.8): xem danh sách hồ sơ là một việc, mở **ảnh mặt thật và số
     * giấy tờ đầy đủ** của một người là việc khác. Tài khoản mẫu có cả hai để soát được màn thẩm
     * định; bỏ `EKYC_REVIEW.READ` đi là cách nhanh nhất để kiểm nhánh ẩn vùng ảnh.
     */
    'EKYC_DASHBOARD.READ',
    'EKYC_SESSION.READ',
    'EKYC_SESSION.CREATE',
    'EKYC_REVIEW.READ',
    'EKYC_REVIEW.UPDATE',
    'EKYC_WORKFLOW.READ',
    'EKYC_WORKFLOW.CREATE',
    'EKYC_WORKFLOW.UPDATE',
    'EKYC_WORKFLOW.DELETE',
    'EKYC_APP.READ',
    'EKYC_APP.CREATE',
    'EKYC_APP.UPDATE',
  ],
  organizationId: 'org-002',
  // Công ty TNHH Thương mại Minh Long — tenant mẫu có allowlist chạy thử.
  tenantId: '0a7d3c5e-1f2b-4e6a-9c8d-000000000002',
};

/**
 * Nhân viên vận hành nền tảng: thấy tenant và nhật ký, **không** sửa được gì.
 *
 * Bộ quyền hẹp này là thứ kiểm được hai bẫy ở §16 của tài liệu tham khảo: tab
 * `Nhật ký` hiện đúng theo quyền, và nút `Sửa cấu hình` phải biến mất.
 */
export const MOCK_VAN_HANH: UserProfile = {
  id: 'u-003',
  fullName: 'Lê Minh Cường',
  email: 'cuong.le@foxai.vn',
  // Khoá vai trò nền tảng, không phải chữ tự do: `lib/pham-vi-cong` đọc tập
  // này để xếp cổng, nên một nhãn tự do sẽ đẩy nhân sự nền tảng sang cổng
  // tenant và họ mất sạch màn quản trị.
  roles: ['vaiTro.kiemToan'],
  // Chỉ đọc cấu hình bảo mật, đầu mối, chính sách nền tảng: kiểm form chỉ xem (ADR-0059).
  permissions: [
    'TENANT.READ',
    'AUDIT_EVENTS.READ',
    'VIEC.READ',
    'SECURITY_CONFIG.READ',
    'TENANT_CONTACT.READ',
    'PLATFORM_POLICY.READ',
    // ADR-0083: xem danh mục model AI và tab Model AI của tenant, không có công tắc.
    'AI_MODEL.READ',
  ],
  organizationId: 'org-001',
};

/**
 * Người cấp license (`LICENSE_ISSUER`, ADR-0058): soạn và gửi được, **không**
 * duyệt hay thu hồi được.
 *
 * Cặp với `admin…` để thử luồng bốn mắt: đăng nhập tài khoản này gửi một yêu
 * cầu cần duyệt, rồi đăng nhập `admin…` để duyệt. Yêu cầu admin tự gửi thì
 * chính admin không thấy nút duyệt.
 */
export const MOCK_NGUOI_CAP_LICENSE: UserProfile = {
  id: 'u-005',
  fullName: 'Vũ Hoàng Nam',
  email: 'nam.vu@foxai.vn',
  roles: ['vaiTro.phuTrachLicense'],
  permissions: ['TENANT.READ', 'GOI.READ', 'LICENSE.READ', 'LICENSE.ISSUE'],
  organizationId: 'org-001',
};

export interface Chatbot {
  id: string;
  name: string;
  active: boolean;
  /** Bật rồi vẫn có thể không còn nguồn nào để trả lời. */
  answerable: boolean;
}

/**
 * Ba trạng thái bot, không phải hai (§8.1): `active` một mình là không đủ, vì
 * một bot có thể đang bật mà không còn nguồn nào để trả lời.
 */
export const MOCK_CHATBOTS: Chatbot[] = [
  { id: 'bot-001', name: 'Trợ lý Kế toán', active: true, answerable: true },
  { id: 'bot-002', name: 'Trợ lý Nhân sự', active: true, answerable: true },
  { id: 'bot-003', name: 'Trợ lý Pháp chế nội bộ', active: true, answerable: false },
  { id: 'bot-004', name: 'Trợ lý Kinh doanh (ngừng dùng)', active: false, answerable: false },
];

export interface DashboardCounters {
  tongChungTu: number;
  soMauOcr: number;
  boTriThuc: number;
  tepTaiLieu: number;
  chatbotHoatDong: number;
  chatbotDaCauHinh: number;
  nguoiDungHoatDong: number;
  tongTaiKhoan: number;
  chungTuNhap: number;
  chungTuDaXacNhan: number;
  chungTuDaChuyenKho: number;
  soVaiTro: number;
  soPhongBan: number;
}

export const MOCK_DASHBOARD: DashboardCounters = {
  tongChungTu: 1284,
  soMauOcr: 12,
  boTriThuc: 38,
  tepTaiLieu: 2416,
  chatbotHoatDong: 2,
  chatbotDaCauHinh: 4,
  nguoiDungHoatDong: 47,
  tongTaiKhoan: 63,
  chungTuNhap: 183,
  chungTuDaXacNhan: 742,
  chungTuDaChuyenKho: 359,
  soVaiTro: 9,
  soPhongBan: 6,
};

export interface KnowledgeBase {
  id: string;
  name: string;
  managingDepartmentName: string | null;
  fileCount: number;
}

export const MOCK_KNOWLEDGE_BASES: KnowledgeBase[] = [
  { id: 'kb-001', name: 'Quy trình kế toán nội bộ', managingDepartmentName: 'Phòng Kế toán', fileCount: 412 },
  { id: 'kb-002', name: 'Chính sách nhân sự', managingDepartmentName: 'Phòng Nhân sự', fileCount: 318 },
  { id: 'kb-003', name: 'Hợp đồng mẫu và điều khoản chuẩn áp dụng toàn công ty', managingDepartmentName: 'Phòng Kinh doanh', fileCount: 276 },
  { id: 'kb-004', name: 'Báo cáo tài chính theo quý', managingDepartmentName: 'Phòng Kế toán', fileCount: 198 },
  { id: 'kb-005', name: 'Nghị quyết Ban Giám đốc', managingDepartmentName: 'Ban Giám đốc', fileCount: 154 },
  { id: 'kb-006', name: 'Tài liệu đào tạo nội bộ', managingDepartmentName: 'Phòng Nhân sự', fileCount: 131 },
  { id: 'kb-007', name: 'Hồ sơ nhà cung cấp', managingDepartmentName: null, fileCount: 87 },
];
